import AsyncSelect from 'react-select/async';
import { useField } from 'formik';
import { getCustomStyles } from '@/theme/components/select';
import { NoOptionsMessage } from '@/components/select';
import { useEffect, useState } from 'react';
import { Text } from '@chakra-ui/react';
import { fetchSelectOptions } from '../../helpers/formHelpers';
// import debounce from 'lodash.debounce';
import debounce from 'debounce-promise';
import uniqby from 'lodash.uniqby';
import { customFilterStyles } from '@/design-system/select/customFilterStyles';
import { KText } from '@/ui-library/components/text';

export const AsyncSelectField = ({ item = {}, formik = {} }) => {
  const {
    name,
    options: staticOptions = [],
    isMulti,
    customFormatLabel,
    placeholder,
    label,
    disabled = false,
    components = {},
    validationType,
    menuPlacement = 'auto',
    valueKey = 'value',
    labelKey = 'label',
    fetchConfig = {},
    refreshOptions,
    initialSearchQuery = 'a',
    useInput = false,
    disabledMessage = undefined,
    customNoOptionsMessage,
    newStyles = [],
    customOnChange = () => {},
    loadDefaultOptions = true,
    defaultOptions = [],
    isAppendToBody,
    isNewFilter,
    hint,
    hintProps = {
      color: 'secondary-700',
      fontWeight: 'medium',
      fontSize: '12px',
      mb: '15px',
      mt: '-5px',
    },
    customLoadOptions,
    isClearable = false,
  } = item;

  const [_field, meta, helpers] = useField(name); // eslint-disable-line
  const { setValue, setError } = helpers;
  const { error, value: formikValue } = meta;

  const { submitCount } = formik || {};
  const showError = submitCount > 0 && error;

  const [selectedOptions, setSelectedOptions] = useState([]);
  const [initialFetchedOptions, setAllFetchedOptions] = useState(staticOptions);
  useEffect(() => {
    if (
      formikValue?.length !== 0 &&
      selectedOptions?.length === 0 &&
      defaultOptions?.length !== 0
    ) {
      setSelectedOptions(defaultOptions);
    }
  }, [formikValue]); // eslint-disable-line
  const allOptions = [
    ...staticOptions,
    ...(isMulti ? selectedOptions : [selectedOptions]),
    ...defaultOptions,
  ];
  // eslint-disable-next-line
  let uniqueOptions = allOptions?.map((item) => {
    if (meta?.value?.includes?.(item?.[valueKey])) {
      return item;
    }
  });
  uniqueOptions = uniqby(uniqueOptions, valueKey);
  let formikValueOptions = allOptions?.find(
    (item) => item?.[valueKey] === meta?.value
  );
  const fieldOptions = formikValueOptions?.length
    ? formikValueOptions
    : [formikValueOptions];
  formikValueOptions = uniqby(fieldOptions, valueKey);

  const values = !formikValue
    ? null
    : isMulti
    ? uniqueOptions
    : validationType === 'object'
    ? formikValue
    : formikValueOptions;

  useEffect(() => {
    setValue(isMulti ? [] : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshOptions]);

  useEffect(() => {
    // Load options on component mount
    const loadInitialOptions = async () => {
      // If custom load options function is provided, use it for initial load
      if (customLoadOptions) {
        try {
          const response = await customLoadOptions('', formik?.values);
          setAllFetchedOptions((prevOptions) => {
            const allExistingOptions = [
              ...staticOptions,
              ...prevOptions,
              ...defaultOptions,
            ];

            const uniqueNewOptions = response.filter(
              (newOption) =>
                !allExistingOptions.some(
                  (existingOption) =>
                    existingOption[valueKey] === newOption[valueKey]
                )
            );

            return [...prevOptions, ...uniqueNewOptions];
          });
        } catch (error) {
          console.error('Error fetching options with custom function:', error);
        }
        return;
      }

      // Otherwise use the existing fetchConfig approach
      if (!fetchConfig?.endPoint) {
        console.error('Fetch endpoint is not defined');
        return;
      }

      const endPoint = useInput
        ? fetchConfig.endPoint + initialSearchQuery
        : fetchConfig.endPoint;

      try {
        const response = await fetchSelectOptions({
          ...fetchConfig,
          endPoint,
        });

        // Merge new options with all fetched options, avoiding duplicates
        const updatedOptions = [
          ...initialFetchedOptions,
          ...response.filter(
            (newOption) =>
              !initialFetchedOptions.some(
                (existingOption) =>
                  existingOption[valueKey] === newOption[valueKey]
              )
          ),
        ];

        setAllFetchedOptions(updatedOptions);
      } catch (error) {
        console.error('Error fetching options:', error);
      }
    };
    if (loadDefaultOptions) {
      loadInitialOptions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSearchQuery]);

  const onChange = (selectedOption) => {
    if (isMulti) {
      selectedOption = uniqby([...selectedOption], 'label');
    }
    customOnChange(selectedOption);
    const options = isMulti ? [...selectedOption] : selectedOption;
    setSelectedOptions(options);
    setValue(
      isMulti
        ? selectedOption?.map((item) => item?.[valueKey])
        : selectedOption?.[valueKey]
    );
    setError(null);

    return selectedOption;
  };

  const formatOptionLabel = (option) => {
    if (customFormatLabel) return customFormatLabel(option, labelKey, item);
    return option?.[labelKey];
  };

  const NoOptionsMessageComp = (props) => {
    if (customNoOptionsMessage) return customNoOptionsMessage(props);
    return NoOptionsMessage(props);
  };

  let uniqueDefaultOptionsWithInput = uniqby(
    [...staticOptions, ...initialFetchedOptions, ...defaultOptions],
    valueKey
  );

  const debouncedLoadOptions = debounce(async (input) => {
    // If custom load options function is provided, use it
    if (customLoadOptions) {
      return await customLoadOptions(input, formik?.values);
    }

    // Otherwise use the existing fetchConfig approach
    const endPoint = useInput
      ? fetchConfig?.endPoint + (input || initialSearchQuery)
      : fetchConfig?.endPoint;
    const response = await fetchSelectOptions({
      ...fetchConfig,
      endPoint,
    });
    return response;
  }, 300);

  if (isAppendToBody) {
    return (
      <>
        {hint && <KText {...hintProps}>{hint}</KText>}
        <AsyncSelect
          isMulti={isMulti}
          defaultOptions={useInput ? uniqueDefaultOptionsWithInput : true}
          cacheOptions
          value={
            Array.isArray(values) && values.every((v) => v !== undefined)
              ? values
              : selectedOptions
          }
          loadOptions={debouncedLoadOptions}
          inputId={name}
          menuPortalTarget={document.body}
          className="kula_custom_select__container"
          classNamePrefix="kula_custom_select"
          // menuPosition={'fixed'}
          // menuShouldBlockScroll={true}
          // menuShouldScrollIntoView={true}
          formatOptionLabel={formatOptionLabel}
          hasError={showError}
          menuPlacement={menuPlacement}
          isClearable={isClearable}
          isDisabled={disabled}
          styles={
            !isNewFilter
              ? getCustomStyles(newStyles)
              : customFilterStyles(newStyles)
          }
          components={{
            IndicatorSeparator: null,
            NoOptionsMessage: NoOptionsMessageComp,
            ...components,
          }}
          placeholder={placeholder || `Select ${label}`}
          getOptionLabel={(option) => option?.[labelKey]}
          getOptionValue={(option) => option?.[valueKey]}
          onChange={onChange}
          extraProps={{
            ...item,
          }}
        />
      </>
    );
  }

  return (
    <>
      {hint && <KText {...hintProps}>{hint}</KText>}
      <AsyncSelect
        isMulti={isMulti}
        defaultOptions={useInput ? uniqueDefaultOptionsWithInput : true}
        cacheOptions
        value={values || selectedOptions}
        loadOptions={debouncedLoadOptions}
        inputId={name}
        // menuPosition={'fixed'}
        // menuShouldBlockScroll={true}
        // menuShouldScrollIntoView={true}
        formatOptionLabel={formatOptionLabel}
        className="kula_custom_select__container"
        classNamePrefix="kula_custom_select"
        hasError={showError}
        menuPlacement={menuPlacement}
        isClearable={isClearable}
        isDisabled={disabled}
        styles={!isNewFilter ? getCustomStyles(newStyles) : customFilterStyles}
        components={{
          IndicatorSeparator: null,
          NoOptionsMessage: NoOptionsMessageComp,
          ...components,
        }}
        placeholder={placeholder || `Select ${label}`}
        getOptionLabel={(option) => option?.[labelKey]}
        getOptionValue={(option) => option?.[valueKey]}
        onChange={onChange}
        extraProps={{
          ...item,
        }}
      />

      {disabled && disabledMessage && (
        <Text mt="5px" color="red">
          {disabledMessage}
        </Text>
      )}
    </>
  );
};
